import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../../../firebaseConfig";
import { getWareHouses } from "../../../../helper/functions";

/**
 * Custom hook to manage shopping filters (route partners and warehouses)
 * @returns {Object} Filter state and handlers
 */
export const useShoppingFilters = () => {
  const user = useSelector((state) => state.auth.value);
  const [selectedFilters, setSelectedFilters] = useState({
    routePartnerId: "",
    warehouseId: "",
  });
  const [filterOptions, setFilterOptions] = useState({
    routePartners: [],
    warehouses: [],
  });
  const routePartnersUnsubscribeRef = useRef(null);
  const warehousesUnsubscribeRef = useRef(null);

  // Fetch route partners on mount
  useEffect(() => {
    try {
      const querySnapshot = collection(db, "routePartners");
      const q = query(querySnapshot, where("isDeleted", "==", false));

      routePartnersUnsubscribeRef.current = onSnapshot(q, (snapshot) => {
        const arr = [];
        snapshot.forEach((doc) => {
          arr.push({ ...doc.data(), id: doc.id });
        });
        setFilterOptions((prev) => ({ ...prev, routePartners: arr }));
      });
    } catch (err) {
      console.error("Error fetching route partners:", err);
    }

    return () => {
      if (routePartnersUnsubscribeRef.current) {
        routePartnersUnsubscribeRef.current();
      }
      if (warehousesUnsubscribeRef.current) {
        warehousesUnsubscribeRef.current();
      }
    };
  }, []);

  // Set default filter if user has routePartnerId
  useEffect(() => {
    if (user?.routePartnerId) {
      setSelectedFilters((prev) => ({
        ...prev,
        routePartnerId: user?.routePartnerId,
      }));
      fetchWarehouses(user?.routePartnerId);
    }
  }, [user]);

  // Fetch warehouses based on route partner
  const fetchWarehouses = async (routePartnerId) => {
    if (warehousesUnsubscribeRef.current) {
      warehousesUnsubscribeRef.current();
    }

    if (routePartnerId) {
      try {
        const data = await getWareHouses({ routePartnerId: routePartnerId });
        setFilterOptions((prev) => ({ ...prev, warehouses: data }));
      } catch (error) {
        console.error("Error fetching warehouses:", error);
      }
    } else {
      setFilterOptions((prev) => ({ ...prev, warehouses: [] }));
    }
  };

  // Handle filter changes
  const changeFilterHandler = (filter) => {
    setSelectedFilters((prev) => ({ ...prev, ...filter }));
    if (filter?.routePartnerId !== undefined) {
      fetchWarehouses(filter?.routePartnerId);
    }
  };

  return {
    selectedFilters,
    filterOptions,
    changeFilterHandler,
  };
};

